import useI18n from 'hooks/I18n/useI18n';
import React from 'react';
import PropTypes from 'prop-types';
import Utils from 'modules/utils';
import { useSelector } from 'react-redux';
import { getCountriesState } from 'modules/countries';
import Grid from 'components/Grid.jsx';
import Box from 'components/Box.jsx';
import Button from 'components/Button.jsx';
import {
    AddressInputGroup,
    CheckboxGroup,
    DateInputGroup,
    FormSelectGroup,
    TextInputGroup,
} from 'components/ui/form';
const { linkTo } = Utils;
import PasswordValidationCheck from './PasswordValidationCheck.jsx';
import useRedirectUrl from 'hooks/Navigation/useRedirectUrl.js';

const UserRegistrationForm = ({
    role,
    formData,
    showErrors,
    clientErrors,
    serverErrors,
    edit,
    submit,
    isRegistering,
    isPasswordAtLeastOneLowercase,
    isPasswordAtLeastEightCharacters,
    isPasswordAtLeastOneUppercase,
    isPasswordAtLeastOneDigit,
    isPasswordInputDirty,
}) => {
    const countries = useSelector(getCountriesState).data;
    const { trans, i18n } = useI18n();
    const redirectUrl = useRedirectUrl();
    const isHostOrProvider = role === 'host' || role === 'farm';
    const termsUrl = `/${i18n}/p/terms`;

    const actionLabel = () => {
        switch (role) {
            case 'host':
                return trans('session.subscribe.leader');
            case 'farm':
                return trans('session.subscribe.farm');
            case 'member':
                return trans('session.subscribe.member');
            default:
                return trans('session.subscribe.connectedVisitor');
        }
    };

    return (
        <>
            {role === 'host' && (
                <>
                    <div className="title">{trans('session.alreadyAccount.title')}</div>
                    <Box>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: trans('session.alreadyAccount.text', {
                                    '%url%': linkTo(
                                        redirectUrl ? `login?redirectUrl=${redirectUrl}` : 'login'
                                    ),
                                }),
                            }}
                        />
                    </Box>
                </>
            )}
            <div className="title">{trans('session.createAccount')}</div>
            <div className="box with-padding box--mediumPadding">
                <form>
                    <TextInputGroup
                        name="firstName"
                        inputVariant="inverse"
                        label={trans('global.placeHolder.firstName')}
                        clientErrors={clientErrors.firstName}
                        serverErrors={serverErrors.firstName}
                        showErrors={showErrors}
                        onChange={edit}
                        value={formData.firstName}
                    />

                    <TextInputGroup
                        name="lastName"
                        inputVariant="inverse"
                        label={trans('global.placeHolder.lastName')}
                        clientErrors={clientErrors.lastName}
                        serverErrors={serverErrors.lastName}
                        showErrors={showErrors}
                        onChange={edit}
                        value={formData.lastName}
                    />

                    <TextInputGroup
                        name="email"
                        inputVariant="inverse"
                        label={trans('global.label.email')}
                        clientErrors={clientErrors.email}
                        serverErrors={serverErrors.email}
                        showErrors={showErrors}
                        onChange={edit}
                        value={formData.email}
                    />

                    <TextInputGroup
                        name="confirmEmail"
                        inputVariant="inverse"
                        label={trans('user.emailConfirmation')}
                        clientErrors={clientErrors.confirmEmail}
                        serverErrors={serverErrors.confirmEmail}
                        showErrors={showErrors}
                        onChange={edit}
                        value={formData.confirmEmail}
                    />

                    <TextInputGroup
                        name="plainPassword"
                        inputVariant="inverse"
                        label={trans('user.password')}
                        type="password"
                        clientErrors={clientErrors.plainPassword}
                        serverErrors={serverErrors.plainPassword}
                        showErrors={showErrors}
                        onChange={edit}
                        value={formData.plainPassword}
                        noMargin
                        className="u-mb-2"
                        withEye
                    />
                    <div className="passwordValidationCheckGroup u-flexible-row u-flexible-row--wrap u-mb-4">
                        <PasswordValidationCheck
                            label={trans('formValidation.type.password.lowercase')}
                            showErrors={isPasswordInputDirty}
                            isValid={isPasswordAtLeastOneLowercase}
                        />
                        <PasswordValidationCheck
                            label={trans('formValidation.type.password.eightCharacters')}
                            showErrors={isPasswordInputDirty}
                            isValid={isPasswordAtLeastEightCharacters}
                        />
                        <PasswordValidationCheck
                            label={trans('formValidation.type.password.uppercase')}
                            showErrors={isPasswordInputDirty}
                            isValid={isPasswordAtLeastOneUppercase}
                        />
                        <PasswordValidationCheck
                            label={trans('formValidation.type.password.digit')}
                            showErrors={isPasswordInputDirty}
                            isValid={isPasswordAtLeastOneDigit}
                        />
                    </div>
                    {isHostOrProvider && (
                        <TextInputGroup
                            name="phone"
                            inputVariant="inverse"
                            label={trans('global.phone')}
                            clientErrors={clientErrors.phone}
                            serverErrors={serverErrors.phone}
                            showErrors={showErrors}
                            onChange={edit}
                            value={formData.phone}
                        />
                    )}

                    <AddressInputGroup onChange={edit}>
                        {({
                            countries: _countries,
                            country,
                            zip,
                            cities,
                            onCountryChange,
                            onZipChange,
                            areCitiesReady,
                        }) => (
                            <>
                                <FormSelectGroup
                                    name="country"
                                    isDisabled={countries.length === 0}
                                    inputVariant="inverse"
                                    label={trans('address.country')}
                                    options={_countries}
                                    emptyOption={trans('address.countryChoose')}
                                    value={country}
                                    onChange={onCountryChange}
                                />

                                {isHostOrProvider && (
                                    <TextInputGroup
                                        name="street"
                                        clientErrors={clientErrors.street}
                                        serverErrors={serverErrors.street}
                                        showErrors={showErrors}
                                        inputVariant="inverse"
                                        label={trans('address.numberWay')}
                                        value={formData.street}
                                        onChange={edit}
                                    />
                                )}

                                {country && (
                                    <Grid sm="4|8">
                                        <TextInputGroup
                                            name="zipcode"
                                            inputVariant="inverse"
                                            label={trans('address.zip')}
                                            value={zip}
                                            onChange={onZipChange}
                                        />

                                        <FormSelectGroup
                                            name="city"
                                            clientErrors={clientErrors.city}
                                            serverErrors={serverErrors.city}
                                            showErrors={showErrors}
                                            isDisabled={!areCitiesReady}
                                            emptyOption={
                                                cities.length !== 0
                                                    ? trans('address.cityDefault')
                                                    : trans('user.city.void')
                                            }
                                            inputVariant="inverse"
                                            label={trans('address.city')}
                                            options={cities}
                                            value={formData.city}
                                            onChange={edit}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}
                    </AddressInputGroup>

                    {isHostOrProvider && (
                        <Grid sm="6|6">
                            <DateInputGroup
                                name="birthday"
                                label={trans('global.dateOfBirth')}
                                onChange={edit}
                                value={formData.birthday}
                                clientErrors={clientErrors.birthday}
                                serverErrors={serverErrors.birthday}
                                showErrors={showErrors}
                                inputVariant="inverse"
                            />

                            <FormSelectGroup
                                name="nationality"
                                inputVariant="inverse"
                                clientErrors={clientErrors.nationality}
                                serverErrors={serverErrors.nationality}
                                showErrors={showErrors}
                                label={trans('global.nationality')}
                                emptyOption={trans('address.countryChoose')}
                                options={countries.map(country => ({
                                    label: country.name,
                                    value: country.code,
                                }))}
                                value={formData.nationality}
                                onChange={edit}
                            />
                        </Grid>
                    )}

                    <CheckboxGroup
                        // @TODO (sinewyk) rework Checkbox(Group) to work with name
                        id="hasAcceptedTOS"
                        isChecked={formData.hasAcceptedTOS}
                        onChange={edit}
                        clientErrors={clientErrors.hasAcceptedTOS}
                        serverErrors={serverErrors.hasAcceptedTOS}
                        showErrors={showErrors}
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: trans('user.tos', { '%url%': termsUrl }),
                            }}
                        />
                    </CheckboxGroup>

                    {isHostOrProvider && (
                        <CheckboxGroup
                            id="hasAcceptedSCU"
                            isChecked={formData.hasAcceptedSCU}
                            onChange={edit}
                            clientErrors={clientErrors.hasAcceptedSCU}
                            serverErrors={serverErrors.hasAcceptedSCU}
                            showErrors={showErrors}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: trans('user.scu', { '%url%': termsUrl }),
                                }}
                            />
                        </CheckboxGroup>
                    )}
                    <Button variant="success" block onClick={submit} loading={isRegistering}>
                        {actionLabel()}
                    </Button>
                </form>
            </div>
            {role !== 'host' && (
                <>
                    <div className="title">{trans('session.alreadyAccount.title')}</div>
                    <Box>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: trans('session.alreadyAccount.text', {
                                    '%url%': linkTo(
                                        redirectUrl ? `login?redirectUrl=${redirectUrl}` : 'login'
                                    ),
                                }),
                            }}
                        />
                    </Box>
                </>
            )}
        </>
    );
};

UserRegistrationForm.propTypes = {
    formData: PropTypes.object.isRequired,
    showErrors: PropTypes.bool.isRequired,
    isPasswordInputDirty: PropTypes.bool.isRequired,
    clientErrors: PropTypes.object.isRequired,
    serverErrors: PropTypes.object.isRequired,
    edit: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    isRegistering: PropTypes.bool.isRequired,
    role: PropTypes.string,
    isPasswordAtLeastOneLowercase: PropTypes.bool,
    isPasswordAtLeastEightCharacters: PropTypes.bool,
    isPasswordAtLeastOneUppercase: PropTypes.bool,
    isPasswordAtLeastOneDigit: PropTypes.bool,
};

export default UserRegistrationForm;
